import React from "react";

/*
 * Driver app illustration (white background, on-brand).
 * A phone mockup with today's task list, live navigation strip and
 * one-tap proof of delivery, sitting on the same light street grid.
 */

const tasks = [
  { n: 1, title: "Al Wasl Rd, Villa 12", time: "09:10", status: "done" },
  { n: 2, title: "Marina Walk, Tower 3", time: "09:40", status: "active" },
  { n: 3, title: "JLT Cluster D, 1402", time: "10:05", status: "next" },
  { n: 4, title: "Business Bay, Bay Sq.", time: "10:30", status: "next" },
];

const font = "Inter, ui-sans-serif, system-ui, sans-serif";
const display = "'Space Grotesk', Inter, sans-serif";

const DriverAppIllustration = ({ className = "" }) => (
  <svg
    viewBox="0 0 480 400"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Driver mobile app showing today's tasks, navigation and proof of delivery"
    data-testid="driver-app-illustration"
  >
    <defs>
      <pattern id="da-grid" width="24" height="24" patternUnits="userSpaceOnUse">
        <path d="M24 0H0V24" fill="none" stroke="#EEEEE9" strokeWidth="1" />
      </pattern>
      <filter id="da-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0E0E10" floodOpacity="0.10" />
      </filter>
      <filter id="da-shadow-lg" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="14" stdDeviation="14" floodColor="#0E0E10" floodOpacity="0.16" />
      </filter>
      <clipPath id="da-screen">
        <rect x="163" y="46" width="154" height="330" rx="18" />
      </clipPath>
    </defs>

    <rect width="480" height="400" fill="#FFFFFF" />
    <rect width="480" height="400" fill="url(#da-grid)" />

    {/* streets (same language as the other illustrations) */}
    <g stroke="#E6E6E0" strokeWidth="10" strokeLinecap="round" fill="none">
      <path d="M0 210 H480" />
      <path d="M150 0 V400" />
      <path d="M340 0 V400" />
      <path d="M0 110 Q 240 90 480 130" />
      <path d="M0 330 Q 240 300 480 340" />
    </g>
    <g stroke="#F1F1EC" strokeWidth="4" strokeLinecap="round" fill="none">
      <path d="M60 0 V400" />
      <path d="M250 0 V400" />
      <path d="M430 0 V400" />
      <path d="M0 50 H480" />
      <path d="M0 270 H480" />
    </g>

    {/* route hint behind the phone */}
    <path d="M62 318 C 120 260, 200 230, 280 190 S 380 120, 420 92" fill="none" stroke="#EDEAFF" strokeWidth="10" strokeLinecap="round" />
    <path d="M62 318 C 120 260, 200 230, 280 190 S 380 120, 420 92" fill="none" stroke="#4C30FF" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="6 8" opacity="0.7" />

    {/* depot + destination pin */}
    <g transform="translate(62 318)" filter="url(#da-shadow)">
      <rect x="-16" y="-16" width="32" height="32" rx="9" fill="#0E0E10" />
      <path d="M-7 3 V-3 L0 -8 L7 -3 V3 Z" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="-2.5" y="-1" width="5" height="4" fill="#FFFFFF" />
    </g>
    <g transform="translate(420 92)" filter="url(#da-shadow)">
      <path d="M0 8 C -7 0, -7 -6, 0 -12 C 7 -6, 7 0, 0 8 Z" fill="#FFFFFF" stroke="#4C30FF" strokeWidth="2" />
      <circle cy="-4" r="2" fill="#4C30FF" />
    </g>

    {/* rider on the route */}
    <g filter="url(#da-shadow)">
      <g>
        <animateMotion dur="10s" repeatCount="indefinite" rotate="auto" path="M62 318 C 120 260, 200 230, 280 190 S 380 120, 420 92" />
        <circle cx="-6" cy="4" r="4" fill="none" stroke="#0E0E10" strokeWidth="2" />
        <circle cx="6" cy="4" r="4" fill="none" stroke="#0E0E10" strokeWidth="2" />
        <path d="M-6 4 L-1 -4 L5 -4 L6 4 M-1 -4 L1 4" fill="none" stroke="#0E0E10" strokeWidth="2" strokeLinejoin="round" />
        <rect x="-3" y="-8" width="7" height="5" rx="1.5" fill="#4C30FF" />
      </g>
    </g>
    {/* phone */}
    <g filter="url(#da-shadow-lg)">
      <rect x="156" y="38" width="168" height="346" rx="26" fill="#0E0E10" />
      <rect x="163" y="46" width="154" height="330" rx="18" fill="#FBFBF9" />
      <rect x="215" y="52" width="50" height="6" rx="3" fill="#0E0E10" />
    </g>

    <g clipPath="url(#da-screen)">
      {/* header */}
      <text x="176" y="80" fontFamily={font} fontSize="8" fontWeight="600" fill="#6B6B73" letterSpacing="1.2">
        TODAY · ROUTE A
      </text>
      <text x="176" y="98" fontFamily={display} fontSize="14" fontWeight="700" fill="#0E0E10">
        Hi Rashid, 12 tasks
      </text>

      {/* progress */}
      <rect x="176" y="108" width="128" height="6" rx="3" fill="#E6E6E0" />
      <rect x="176" y="108" width="0" height="6" rx="3" fill="#4C30FF">
        <animate attributeName="width" values="0;64;64" keyTimes="0;0.5;1" dur="5s" repeatCount="indefinite" />
      </rect>

      {/* task list */}
      {tasks.map((t, i) => {
        const y = 126 + i * 44;
        const active = t.status === "active";
        const done = t.status === "done";
        return (
          <g key={t.n}>
            <rect
              x="172"
              y={y}
              width="136"
              height="38"
              rx="10"
              fill={active ? "#0E0E10" : "#FFFFFF"}
              stroke={active ? "#0E0E10" : "#E6E6E0"}
            />
            {/* status dot / number */}
            <circle cx="188" cy={y + 19} r="8" fill={done ? "#4C30FF" : active ? "#4C30FF" : "#F4F4F0"} />
            {done ? (
              <path d={`M184 ${y + 19} l3 3 l5 -6`} fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <text x="188" y={y + 22} textAnchor="middle" fontFamily={font} fontSize="8" fontWeight="700" fill={active ? "#FFFFFF" : "#3A3A40"}>
                {t.n}
              </text>
            )}
            <text x="202" y={y + 16} fontFamily={font} fontSize="8.5" fontWeight="600" fill={active ? "#FFFFFF" : "#0E0E10"}>
              {t.title}
            </text>
            <text x="202" y={y + 28} fontFamily={font} fontSize="7.5" fill={active ? "rgba(255,255,255,0.65)" : "#6B6B73"}>
              {done ? "Delivered · Signed" : active ? "Navigating · 4 min" : `ETA ${t.time}`}
            </text>
            {active && (
              <g transform={`translate(296 ${y + 19})`}>
                <circle r="6" fill="#4C30FF" />
                <path d="M-2 0 L0 -3 L2 0 L0 3 Z" fill="#FFFFFF" />
              </g>
            )}
          </g>
        );
      })}

      {/* POD button */}
      <rect x="172" y="312" width="136" height="34" rx="17" fill="#4C30FF">
        <animate attributeName="opacity" values="1;0.85;1" dur="2.4s" repeatCount="indefinite" />
      </rect>
      <text x="240" y="333" textAnchor="middle" fontFamily={font} fontSize="9" fontWeight="600" fill="#FFFFFF">
        Confirm delivery
      </text>

      {/* bottom nav */}
      <rect x="163" y="356" width="154" height="20" fill="#FFFFFF" />
      <line x1="163" y1="356" x2="317" y2="356" stroke="#E6E6E0" />
      {[192, 224, 256, 288].map((x, i) => (
        <rect key={x} x={x - 5} y="362" width="10" height="8" rx="2" fill={i === 0 ? "#4C30FF" : "#D6D6CE"} />
      ))}
    </g>

    {/* floating: signature captured */}
    <g transform="translate(24 24)" filter="url(#da-shadow)">
      <rect width="140" height="58" rx="12" fill="#FFFFFF" stroke="#E6E6E0" />
      <text x="14" y="21" fontFamily={font} fontSize="8.5" fontWeight="600" fill="#6B6B73" letterSpacing="1.1">
        PROOF OF DELIVERY
      </text>
      <path d="M14 42 c 8 -12, 14 8, 22 -4 s 12 -6, 20 2 s 14 -10, 22 -2 s 12 6, 24 -6" fill="none" stroke="#0E0E10" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="126" cy="16" r="5" fill="#25D366" />
      <path d="M123.5 16 l1.8 1.8 l3 -3.6" fill="none" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </g>

    {/* floating: OTP */}
    <g transform="translate(346 60)" filter="url(#da-shadow)">
      <rect width="110" height="44" rx="12" fill="#0E0E10" />
      <text x="12" y="17" fontFamily={font} fontSize="8" fontWeight="600" fill="rgba(255,255,255,0.6)" letterSpacing="1.2">
        OTP VERIFIED
      </text>
      <text x="12" y="35" fontFamily={display} fontSize="15" fontWeight="700" fill="#FFFFFF" letterSpacing="3">
        4 8 1 9
      </text>
    </g>

    {/* floating: live sync */}
    <g transform="translate(346 300)" filter="url(#da-shadow)">
      <rect width="110" height="30" rx="15" fill="#FFFFFF" stroke="#E6E6E0" />
      <circle cx="16" cy="15" r="4" fill="#4C30FF">
        <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
      </circle>
      <text x="27" y="19" fontFamily={font} fontSize="10" fontWeight="600" fill="#0E0E10">
        Live · synced
      </text>
    </g>

  </svg>
);

export default DriverAppIllustration;
